/**
 * Shared ZIP parsing utility for client-portal export ZIPs.
 * Used by ImportExportTab (import with diff) and CreateTenantWizard.
 */

export const FILE_KEY_MAP: Record<string, string> = {
  'tenant.json': 'tenant',
  'task.json': 'task',
  'cohort.json': 'cohort',
  'cms.json': 'cms',
  'fis.json': 'fis',
  'sweepstakes.json': 'sweepstakes',
  'adventure.json': 'adventure',
  'taskrewardcollection.json': 'taskrewardcollection',
  'wallet.json': 'wallet',
  'admin.json': 'admin',
  // notification.json intentionally omitted — notification config is no longer
  // exported/imported, so any notification.json in an older ZIP is skipped.
  'metadata.json': 'metadata',
  'content.json': 'content',
  'color.json': 'color',
  'taskv2.json': 'taskv2',
  'lifecycle.json': 'lifecycle',
};

export interface ParsedZipResult {
  parsedFiles: Record<string, any>;
  fileList: string[];
}

/**
 * Parse a client-portal export ZIP file and extract all JSON files.
 * Returns parsed data keyed by FILE_KEY_MAP values and a list of found filenames.
 */
export async function parseExportZip(file: File): Promise<ParsedZipResult> {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(file);

  const parsedFiles: Record<string, any> = {};
  const fileList: string[] = [];

  for (const [name, zipEntry] of Object.entries(zip.files)) {
    if (zipEntry.dir || !name.endsWith('.json')) continue;
    const baseName = name.split('/').pop() || name;
    const key = FILE_KEY_MAP[baseName.toLowerCase()];
    if (!key) continue;
    try {
      const text = await zipEntry.async('text');
      const json = JSON.parse(text);
      parsedFiles[key] = json.Data || json.data || json;
      fileList.push(baseName);
    } catch { /* skip unparseable files */ }
  }

  return { parsedFiles, fileList };
}
